import React, { useEffect, useState } from "react";
import { agentApi } from "../../services/agentApi";
import { STEP_KINDS, getStepKindInfo } from "../../constants/agentPlaybookSteps";
import PlaybookVariableHint from "./playbookVariables";

const BASE_TEXT_HINT =
  "Frases prontas são enviadas como estão, só com as variáveis preenchidas, sem passar pela IA. Instruções (objeção, horários, confirmação) passam pela IA e limitam o que a mensagem pode dizer.";

const EMPTY_STEP_FORM = {
  code: "",
  label: "",
  kind: "INFORM",
  promptText: "",
};

export default function PlaybookStepsEditor({ playbookId, readOnly = false }) {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [reordering, setReordering] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await agentApi.steps.list(playbookId);
      setSteps(r.steps || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [playbookId]);

  async function handleSave(step) {
    try {
      await agentApi.steps.patch(playbookId, step.id, {
        label: step.label || null,
        promptText: step.promptText,
        nextStep: step.nextStep,
        branchRules: step.branchRules,
        kind: step.kind,
        llmModel: step.llmModel,
        extractField: step.extractField,
      });
      await load();
    } catch (err) {
      alert(`Erro: ${err.message}`);
    }
  }

  async function moveStep(from, to) {
    if (readOnly || reordering) return;
    if (from === to || to < 0 || to >= steps.length) return;

    const previous = steps;
    const next = [...steps];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);

    setSteps(next.map((s, i) => ({ ...s, order: i + 1 })));
    setReordering(true);
    try {
      const r = await agentApi.steps.reorder(playbookId, next.map((s) => s.id));
      if (r?.steps) setSteps(r.steps);
    } catch (err) {
      setSteps(previous);
      alert(`Erro ao reordenar: ${err.message}`);
    } finally {
      setReordering(false);
    }
  }

  function handleDrop(index) {
    if (dragIndex !== null) moveStep(dragIndex, index);
    setDragIndex(null);
    setDragOverIndex(null);
  }

  async function handleDelete(stepId) {
    const step = steps.find((s) => s.id === stepId);
    const referencedBy = steps.filter(
      (s) =>
        s.id !== stepId &&
        (s.nextStep === step?.code ||
          (s.branchRules || []).some((rule) => rule?.goTo === step?.code)),
    );
    const links = referencedBy.length
      ? `\n\n${referencedBy.map((s) => s.code).join(", ")} aponta para este step. Essa ligação será removida.`
      : "";
    if (!confirm(`Excluir o step ${step?.code || ""}?${links}`)) return;
    try {
      await agentApi.steps.delete(playbookId, stepId);
      if (expanded === stepId) setExpanded(null);
      await load();
    } catch (err) {
      const data = err.response?.data;
      const message =
        (typeof data?.message === "string" && data.message) ||
        (typeof data?.error === "string" && data.error) ||
        err.message ||
        "Erro ao excluir";
      alert(message);
    }
  }

  async function handleAdd(form) {
    const code = form.code.trim().toUpperCase().replace(/\s+/g, "_");
    if (!code) {
      alert("Informe o código do step.");
      return;
    }
    try {
      await agentApi.steps.create(playbookId, {
        code,
        label: form.label.trim() || null,
        order: steps.length + 1,
        kind: form.kind,
        promptText: form.promptText,
        nextStep: null,
        branchRules: [],
      });
      setShowAddModal(false);
      await load();
    } catch (err) {
      alert(`Erro: ${err.message}`);
    }
  }

  if (loading) return <div className="text-gray-500">Carregando...</div>;
  if (error) return <div className="text-red-600">{error}</div>;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3">
        <p className="text-sm font-medium text-blue-900 mb-2">Tipos de step</p>
        <div className="grid gap-2 sm:grid-cols-2">
          {STEP_KINDS.map((k) => (
            <div key={k.value} className="text-xs text-blue-800">
              <span className="font-semibold">{k.label}</span>
              <span className="text-blue-600/70"> ({k.value})</span>
              <span className="text-blue-700"> — {k.description}</span>
            </div>
          ))}
        </div>
        {!readOnly && (
          <p className="mt-2 text-xs text-blue-700">
            Arraste pela alça ou use as setas para reordenar.
            A ordem organiza a leitura do playbook; a execução segue o campo "Próximo step" e as
            regras de ramificação.
          </p>
        )}
      </div>

      <div className={`space-y-2 ${reordering ? "opacity-70" : ""}`}>
        {steps.map((s, index) => (
          <StepCard
            key={s.id}
            step={s}
            index={index}
            total={steps.length}
            allCodes={steps.map((x) => x.code)}
            expanded={expanded === s.id}
            onToggle={() => setExpanded(expanded === s.id ? null : s.id)}
            onSave={handleSave}
            onDelete={handleDelete}
            onMove={moveStep}
            readOnly={readOnly}
            isDragging={dragIndex === index}
            isDragTarget={dragOverIndex === index && dragIndex !== index}
            onDragStart={() => setDragIndex(index)}
            onDragEnter={() => dragIndex !== null && setDragOverIndex(index)}
            onDragEnd={() => { setDragIndex(null); setDragOverIndex(null); }}
            onDrop={() => handleDrop(index)}
          />
        ))}
        {!readOnly && (
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            className="w-full py-3 border-2 border-dashed border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-600 rounded-md transition-colors"
          >
            + Adicionar step
          </button>
        )}
      </div>

      {showAddModal && (
        <AddStepModal
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAdd}
        />
      )}
    </div>
  );
}

function AddStepModal({ onClose, onSubmit }) {
  const [form, setForm] = useState(EMPTY_STEP_FORM);
  const [saving, setSaving] = useState(false);
  const kindInfo = getStepKindInfo(form.kind);

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await onSubmit(form);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl border border-gray-200">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Novo step</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Código
            </label>
            <input
              type="text"
              required
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder="Ex: NEW_STEP"
              className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 rounded-md text-sm font-mono focus:outline-none focus:border-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Identificador único em maiúsculas (ex: OFFER_MEETING).
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrição
            </label>
            <input
              type="text"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="Ex: Oferta de reunião com trader"
              className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 rounded-md text-sm focus:outline-none focus:border-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Rótulo em português explicando o propósito deste step no fluxo.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo
            </label>
            <select
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value })}
              className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 rounded-md text-sm focus:outline-none focus:border-blue-500"
            >
              {STEP_KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label} ({k.value})
                </option>
              ))}
            </select>
            {kindInfo.description && (
              <p className="mt-1.5 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-2 py-1.5">
                {kindInfo.description}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Texto base
            </label>
            <textarea
              rows={3}
              value={form.promptText}
              onChange={(e) => setForm({ ...form, promptText: e.target.value })}
              placeholder="Mensagem pronta ou instrução deste step..."
              className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 rounded-md text-sm font-mono focus:outline-none focus:border-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">{BASE_TEXT_HINT}</p>
            <PlaybookVariableHint className="mt-1" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm rounded-md transition-colors"
            >
              {saving ? "Criando..." : "Criar step"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function StepCard({
  step,
  index,
  total,
  allCodes,
  expanded,
  onToggle,
  onSave,
  onDelete,
  onMove,
  readOnly = false,
  isDragging = false,
  isDragTarget = false,
  onDragStart,
  onDragEnter,
  onDragEnd,
  onDrop,
}) {
  const [draft, setDraft] = useState({ ...step, branchRules: step.branchRules || [] });
  const [dragEnabled, setDragEnabled] = useState(false);
  useEffect(() => setDraft({ ...step, branchRules: step.branchRules || [] }), [step.id]);

  const kindInfo = getStepKindInfo(draft.kind);

  function updateRule(idx, key, val) {
    const rules = [...draft.branchRules];
    rules[idx] = { ...rules[idx], [key]: val };
    setDraft({ ...draft, branchRules: rules });
  }
  function addRule() {
    setDraft({ ...draft, branchRules: [...draft.branchRules, { if: "intent=", goTo: "" }] });
  }
  function removeRule(idx) {
    setDraft({ ...draft, branchRules: draft.branchRules.filter((_, i) => i !== idx) });
  }

  return (
    <div
      draggable={dragEnabled}
      onDragStart={onDragStart}
      onDragEnter={onDragEnter}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); setDragEnabled(false); onDrop?.(); }}
      onDragEnd={() => { setDragEnabled(false); onDragEnd?.(); }}
      className={`border rounded-md bg-white shadow-sm transition-colors ${
        isDragTarget ? "border-blue-400 ring-2 ring-blue-200" : "border-gray-200"
      } ${isDragging ? "opacity-50" : ""}`}
    >
      <div className="flex items-start gap-1 p-3 hover:bg-gray-50 rounded-md transition-colors">
        {!readOnly && (
          <span
            onMouseDown={() => setDragEnabled(true)}
            onMouseUp={() => setDragEnabled(false)}
            title="Arraste para reordenar"
            className="shrink-0 px-1 py-0.5 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing select-none"
          >
            ⠿
          </span>
        )}
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 min-w-0 text-left"
        >
          <div className="text-gray-900 font-medium">
            <span className="text-gray-400 mr-1">#{step.order}</span>
            {step.code}
            {step.label && (
              <span className="text-gray-600 font-normal ml-2">— {step.label}</span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
              {kindInfo.label}
            </span>
            <span className="text-xs text-gray-500">{kindInfo.description}</span>
          </div>
        </button>
        {!readOnly && (
          <div className="flex shrink-0 items-center gap-0.5">
            <button
              type="button"
              onClick={() => onMove(index, index - 1)}
              disabled={index === 0}
              title="Mover para cima"
              aria-label="Mover step para cima"
              className="px-1.5 py-0.5 text-xs text-gray-400 hover:text-blue-600 disabled:opacity-30 disabled:hover:text-gray-400"
            >
              ↑
            </button>
            <button
              type="button"
              onClick={() => onMove(index, index + 1)}
              disabled={index === total - 1}
              title="Mover para baixo"
              aria-label="Mover step para baixo"
              className="px-1.5 py-0.5 text-xs text-gray-400 hover:text-blue-600 disabled:opacity-30 disabled:hover:text-gray-400"
            >
              ↓
            </button>
          </div>
        )}
        <button
          type="button"
          onClick={onToggle}
          className="shrink-0 px-1 pt-0.5 text-gray-400 text-xs"
          aria-label={expanded ? "Recolher step" : "Expandir step"}
        >
          {expanded ? "▲" : "▼"}
        </button>
      </div>
      {expanded && (
        <div className="p-3 border-t border-gray-200 bg-gray-50 space-y-3 rounded-b-md">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Descrição</label>
            <input
              type="text"
              value={draft.label || ""}
              onChange={(e) => setDraft({ ...draft, label: e.target.value })}
              readOnly={readOnly}
              placeholder="Ex: Aguarda resposta do lead"
              className={`w-full px-2 py-1.5 bg-white border border-gray-300 text-gray-900 rounded text-sm focus:outline-none focus:border-blue-500 ${readOnly ? "cursor-default bg-gray-50" : ""}`}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Tipo</label>
            <select
              value={draft.kind}
              onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
              disabled={readOnly}
              className="w-full px-2 py-1.5 bg-white border border-gray-300 text-gray-900 rounded text-sm focus:outline-none focus:border-blue-500 disabled:cursor-default disabled:bg-gray-50"
            >
              {STEP_KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label} ({k.value})
                </option>
              ))}
            </select>
            {kindInfo.description && (
              <p className="mt-1 text-xs text-gray-500">{kindInfo.description}</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Texto base</label>
            <textarea
              rows={3}
              value={draft.promptText}
              onChange={(e) => setDraft({ ...draft, promptText: e.target.value })}
              readOnly={readOnly}
              className={`w-full px-2 py-1.5 bg-white border border-gray-300 text-gray-900 rounded text-sm font-mono focus:outline-none focus:border-blue-500 ${readOnly ? "cursor-default bg-gray-50" : ""}`}
            />
            <p className="mt-1 text-xs text-gray-500">{BASE_TEXT_HINT}</p>
            <PlaybookVariableHint className="mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Próximo step</label>
              <select
                value={draft.nextStep || ""}
                onChange={(e) => setDraft({ ...draft, nextStep: e.target.value || null })}
                disabled={readOnly}
                className="w-full px-2 py-1.5 bg-white border border-gray-300 text-gray-900 rounded text-sm focus:outline-none focus:border-blue-500 disabled:cursor-default disabled:bg-gray-50"
              >
                <option value="">(nenhum / terminal)</option>
                {allCodes.filter((c) => c !== step.code).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Modelo LLM</label>
              <select
                value={draft.llmModel || ""}
                onChange={(e) => setDraft({ ...draft, llmModel: e.target.value || null })}
                disabled={readOnly}
                className="w-full px-2 py-1.5 bg-white border border-gray-300 text-gray-900 rounded text-sm focus:outline-none focus:border-blue-500 disabled:cursor-default disabled:bg-gray-50"
              >
                <option value="">auto</option>
                <option value="MINI">MINI</option>
                <option value="FULL">FULL</option>
              </select>
            </div>
          </div>
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-xs font-medium text-gray-600">Regras de ramificação</label>
              {!readOnly && (
              <button
                type="button"
                onClick={addRule}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                + Adicionar
              </button>
              )}
            </div>
            <div className="space-y-1">
              {draft.branchRules.map((r, i) => (
                <div key={i} className="flex gap-2 items-center bg-white border border-gray-200 rounded px-2 py-1">
                  <input
                    type="text"
                    value={r.if}
                    onChange={(e) => updateRule(i, "if", e.target.value)}
                    placeholder="intent=opt_out"
                    readOnly={readOnly}
                    className={`flex-1 px-1 py-0.5 border-0 text-gray-900 text-xs font-mono focus:outline-none ${readOnly ? "bg-transparent" : ""}`}
                  />
                  <span className="text-gray-400 text-xs">→</span>
                  <select
                    value={r.goTo}
                    onChange={(e) => updateRule(i, "goTo", e.target.value)}
                    disabled={readOnly}
                    className="px-1 py-0.5 border-0 text-gray-900 text-xs focus:outline-none disabled:cursor-default disabled:bg-transparent"
                  >
                    <option value="">--</option>
                    {allCodes.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {!readOnly && (
                  <button
                    type="button"
                    onClick={() => removeRule(i)}
                    className="text-red-400 hover:text-red-600 text-xs px-1"
                  >
                    ×
                  </button>
                  )}
                </div>
              ))}
            </div>
          </div>
          {!readOnly && (
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => onSave(draft)}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md transition-colors"
            >
              Salvar
            </button>
            <button
              type="button"
              onClick={() => onDelete(step.id)}
              className="px-3 py-1.5 bg-white border border-red-200 hover:bg-red-50 text-red-600 text-sm rounded-md transition-colors"
            >
              Excluir
            </button>
          </div>
          )}
        </div>
      )}
    </div>
  );
}
